import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

export interface Facility {
  id: string;
  name: string;
  address: string;
  city: string;
  state: string;
  zip_code: string;
  latitude: number;
  longitude: number;
  tank_count: number;
  has_leaks: boolean;
  county_fips: string;
}

export interface SearchResult extends Facility {
  distance_miles: number;
  pricing?: {
    quotes: CarrierQuote[];
    summary: {
      best_carrier: string;
      lowest_premium: number;
      savings_vs_highest: number;
      average_premium: number;
    };
  };
}

export interface GeocodingResult {
  success: boolean;
  original_address?: string;
  formatted_address?: string;
  latitude?: number;
  longitude?: number;
  message?: string;
}

export interface ReleaseInfo {
  lust_id: string;
  name: string;
  address: string;
  city: string;
  state: string;
  zip_code: string;
  latitude: number;
  longitude: number;
  status: string;
  substance: string;
  reported_date: string | null;
  distance_miles: number;
  within_spa: string;
  within_whpa: string;
  within_floodplain: string;
  population_within_1500ft: number;
  domestic_wells_within_1500ft: number;
}

export interface CarrierQuote {
  carrier: string;
  base_premium: number;
  annual_premium: number;
  coverage_limit: number;
  deductible: number;
}

export interface PricingInfo {
  facility_id: string;
  tank_count: number;
  risk_score: number;
  quotes: CarrierQuote[];
  summary: {
    best_carrier: string;
    lowest_premium: number;
    savings_vs_highest: number;
    average_premium: number;
  };
}

export interface SearchResponse {
  facilities: SearchResult[];
  geocoding: GeocodingResult;
  total_found: number;
  closest_release?: ReleaseInfo;
  pricing?: PricingInfo[];
}

export const logActivity = (
  activityType: string,
  user?: any,
  facilityId?: string,
  lat?: number,
  lon?: number
): void => {
  // Ultra-lightweight logging that won't block anything
  if (!user?.sub) return; // Skip if no user
  
  // Queue the logging to happen after all rendering is complete
  queueMicrotask(() => {
    setTimeout(async () => {
      try {
        // Use fetch directly for minimal overhead
        fetch(`${process.env.REACT_APP_API_URL}/log-activity`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            activity_type: activityType,
            auth0_user_id: user.sub,
            email: user.email,
            name: user.name,
            facility_id: facilityId,
            location_lat: lat,
            location_lon: lon
          })
        }).catch(() => {}); // Silently fail to avoid any console noise
      } catch {}
    }, 2000); // Further delay to ensure no performance impact
  });
};

export const getFacilities = async (
  token: string,
  state?: string,
  countyFips?: string,
  limit: number = 100
): Promise<Facility[]> => {
  try {
    const params = new URLSearchParams();
    if (state) params.append('state', state);
    if (countyFips) params.append('county_fips', countyFips);
    params.append('limit', limit.toString());

    const response = await api.get(`/facilities?${params.toString()}`, {
      headers: {
        Authorization: `Bearer ${token}`
      }
    });
    
    return response.data;
  } catch (error) {
    console.error('Error fetching facilities:', error);
    throw error;
  }
};

export const searchFacilitiesByAddress = async (
  token: string,
  address: string
): Promise<SearchResponse> => {
  try {
    const response = await api.post('/search-address', {
      address
    }, {
      headers: {
        Authorization: `Bearer ${token}`
      }
    });
    
    return response.data;
  } catch (error) {
    console.error('Error searching facilities:', error);
    throw error;
  }
};

export const getStatesSummary = async (token: string) => {
  try {
    const response = await api.get('/states-summary', {
      headers: {
        Authorization: `Bearer ${token}`
      }
    });
    
    return response.data;
  } catch (error) {
    console.error('Error fetching states summary:', error);
    throw error;
  }
};

export const getUserRole = async (token: string): Promise<string> => {
  try {
    // TODO: Implement actual role extraction from JWT token or API call
    // For now, return a default role
    return 'general';
  } catch (error) {
    console.error('Error getting user role:', error);
    return 'general';
  }
};

export const getFacilityDetails = async (
  token: string,
  facilityId: string
): Promise<any> => {
  try {
    const response = await api.get(`/facilities/${facilityId}`, {
      headers: {
        Authorization: `Bearer ${token}`
      }
    });
    
    return response.data;
  } catch (error) {
    console.error('Error fetching facility details:', error);
    throw error;
  }
};

export const getReleaseDetails = async (
  token: string,
  lustId: string
): Promise<any> => {
  try {
    const response = await api.get(`/releases/${lustId}`, {
      headers: {
        Authorization: `Bearer ${token}`
      }
    });
    
    return response.data;
  } catch (error) {
    console.error('Error fetching release details:', error);
    throw error;
  }
};

export const healthCheck = async (): Promise<boolean> => {
  try {
    const response = await api.get('/health');
    return response.data.status === 'healthy';
  } catch (error) {
    console.error('Health check failed:', error);
    return false;
  }
};